import { computed, observable, toJS } from 'mobx';
import { DiscrepancyStatus, Language } from '../services/const';
import { DiscrepancyRequestModel, getBookingResponse } from '../services/types';

export abstract class BookingDetailBase {
  @observable accessor bookingDetail: getBookingResponse = {};
  @observable accessor discrepancyRequests: DiscrepancyRequestModel[] = [];

  @computed
  get booking() {
    return toJS(this.bookingDetail.booking);
  }

  @computed
  get cancelRefundDisplay() {
    return toJS(this.bookingDetail.cancelRefundDisplay);
  }

  @computed
  get denyReason() {
    return this.booking?.requestConfirmDetail?.denyReason;
  }

  @computed
  get bookingId() {
    return this.booking?.id;
  }

  @computed
  get bookingCustomId() {
    return this.booking?.bookingCustomId;
  }
  @computed
  get voucherUrls() {
    return toJS(this.bookingDetail.voucherUrls) || {};
  }

  @computed
  get info() {
    return toJS(this.booking?.bookingInfo) || {};
  }

  @computed
  get status() {
    return this.booking?.status;
  }

  @computed
  get issuedStatus() {
    return this.booking?.issuedStatus;
  }

  @computed
  get activityLanguage() {
    return this.info?.activityLanguage || Language.English;
  }

  @computed
  get bookingAt() {
    return this.booking?.bookingAt;
  }

  @computed
  get totalPriceForBooking() {
    return this.info?.totalPriceForBooking;
  }

  @computed
  get otaExchangePrice() {
    return this.info?.otaExchangePrice;
  }

  @computed
  get reviewedDiscrepancyRequests() {
    return this.discrepancyRequests.filter(discrepancy => {
      return discrepancy.status === DiscrepancyStatus.Approved;
    });
  }

  @computed
  get lastDiscrepancy() {
    const lastDiscrepancy = this.discrepancyRequests[0] || {};
    if (
      lastDiscrepancy.status === DiscrepancyStatus.Approved ||
      lastDiscrepancy.status === DiscrepancyStatus.Rejected
    ) {
      // if the last discrepancy is already reviewed, it means there is no ongoing discrepancy
      return {};
    }
    return lastDiscrepancy;
  }

  @computed
  get discrepancyStatus() {
    return this.lastDiscrepancy.status;
  }
  @computed
  get discrepancyInitiator() {
    return this.lastDiscrepancy.initiator;
  }
  @computed
  get hasApprovedDiscrepancy() {
    return this.discrepancyRequests.some(discrepancy => {
      return (
        discrepancy.status === DiscrepancyStatus.Approved ||
        discrepancy.status === DiscrepancyStatus.Cancelled
      );
    });
  }
  @computed
  get bookingType() {
    return this.booking?.bookingType;
  }

  @computed
  get cancelReason() {
    return this.booking?.cancelBookingRequest?.cancelReason;
  }
  @computed
  get failedReason() {
    return this.booking?.cancelBookingRequest?.failedReason;
  }

  @computed
  get participantInfo() {
    return toJS(this.info.participantInfo);
  }

  @computed
  get parentBooking() {
    return this.bookingDetail.booking?.parentBooking;
  }

  @computed
  get bundleChildBookingIds() {
    return toJS(this.bookingDetail.booking?.bundleChildBookingIds) || [];
  }

  @computed
  get hasDiscrepancy() {
    return this.bookingDetail.booking?.hasDiscrepancy;
  }

  @computed
  get cancelBookingRequestStatus() {
    return this.bookingDetail.booking?.cancelBookingRequest?.status;
  }

  @computed
  get cancelBookingCommon() {
    return this.bookingDetail.booking?.cancelBookingRequest?.comment;
  }

  @computed
  get cancelBy() {
    return this.bookingDetail.booking?.cancelBookingRequest?.cancelBy;
  }

  @computed
  get canceller() {
    return this.bookingDetail.booking?.cancelBookingRequest?.canceller;
  }

  @computed
  get supplierBookingId() {
    return this.bookingDetail.booking?.supplierBookingId;
  }

  @computed
  get agentBookingId() {
    return this.bookingDetail.booking?.agentBookingId;
  }

  @computed
  get agentReservationNote() {
    return this.bookingDetail.booking?.agentReservationNote;
  }
}
